"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useState } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";
import { LuCircleHelp as LuHelpCircle, LuRotateCw } from "react-icons/lu";

import FeedbackWidget from "@/components/app/app-layout/sidebar-top-resutls-widget/sidebar-feedback-widget";
import { Button } from "@/components/ui/button";
import { getPostData } from "@/utils/post-utils";

import type { PostType } from "./feed-context";

type CommonPostContextProps = {
  onUserBlock?: (publicKey: string) => void;
  isPinned?: boolean;
  hideStatusRow?: boolean;
  setPinnedPostAssociationId?: (id: string | null) => void;
  pinnedPostAssociationId?: string | null;
  setPinnedPost?: (post: PostType | null) => void;
  disableReadMore?: boolean;
  hideMedia?: boolean;
  hideEmbeds?: boolean;
  hideHeader?: boolean;
  hideActivity?: boolean;
  postCachePrefix?: string;
  showMoreMaxHeight?: number;
  hideAvatar?: boolean;
  hideUpvote?: boolean;
  hideHeaderActions?: boolean;
  forwardContextProps?: boolean;
};

type PostContextType = {
  post: PostType;
  postHash: string;
  rootPostHash?: string | null;
  setRootPostHash: (hash: string | null) => void;
  updatePost: (
    hash: string,
    updatedPost: PostType | ((prev: PostType) => PostType),
  ) => void;
  isNSFW: boolean;
  posterIsNSFW: boolean;
  posterIsGreylisted: boolean;
  posterIsBlacklisted: boolean;
  isComment: boolean;
} & CommonPostContextProps &
  ReturnType<typeof getPostData>;

const PostContext = createContext<PostContextType>(null!);

const DEFAULT_SHOW_MORE_MAX_HEIGHT = 750;

export const usePostContext = () => {
  return useContext(PostContext);
};

type PostContextProviderProps = {
  post: PostType;
  children: ReactNode;
  updatePostByHash?: (
    hash: string,
    updatedPost: PostType | ((prev: PostType) => PostType),
  ) => void;
} & CommonPostContextProps;

type PostErrorFallbackProps = FallbackProps & {
  postHash: string;
};

const PostErrorFallback = ({
  resetErrorBoundary,
  postHash,
}: PostErrorFallbackProps) => {
  return (
    <div
      className="
        flex flex-col items-center justify-center rounded-md p-6 text-gray-500
      "
    >
      <p className="mb-4 text-center">
        This post content is temporarily unavailable.
      </p>
      <div className="flex items-center gap-3">
        <Button
          onClick={resetErrorBoundary}
          className="flex items-center gap-2"
        >
          <LuRotateCw size={16} />
          Refresh post
        </Button>

        <Button asChild variant="outline" className="flex items-center gap-2">
          <FeedbackWidget customCurrentPage={`/post/${postHash}`}>
            <LuHelpCircle size={16} />
            Send Feedback
          </FeedbackWidget>
        </Button>
      </div>
    </div>
  );
};

export const PostContextProvider = ({
  post,
  children,
  onUserBlock,
  updatePostByHash,
  isPinned = false,
  setPinnedPostAssociationId,
  setPinnedPost,
  pinnedPostAssociationId = null,
  hideStatusRow = false,
  disableReadMore = false,
  hideMedia = false,
  hideEmbeds = false,
  hideHeader = false,
  hideAvatar = false,
  hideUpvote = false,
  hideHeaderActions = false,
  hideActivity = false,
  forwardContextProps = false,
  postCachePrefix,
  showMoreMaxHeight = DEFAULT_SHOW_MORE_MAX_HEIGHT,
}: PostContextProviderProps) => {
  const [rootPostHash, setRootPostHash] = useState<string | null>(null);
  const postHash = post?.postHash;

  if (!postHash) {
    throw new Error("Post hash is required");
  }

  const updatePost = useCallback(
    (hash: string, updatedPost: PostType | ((prev: PostType) => PostType)) => {
      if (updatePostByHash) {
        updatePostByHash(hash, updatedPost);
      }
    },
    [updatePostByHash],
  );

  const handleError = (error: unknown) => {
    console.error(`Post with hash ${postHash} crashed:`, error);
  };

  return (
    <ErrorBoundary
      fallbackRender={(props) => (
        <PostErrorFallback {...props} postHash={postHash} />
      )}
      onError={handleError}
    >
      <PostContext.Provider
        value={{
          rootPostHash,
          setRootPostHash,
          setPinnedPost,
          setPinnedPostAssociationId,
          pinnedPostAssociationId,
          post,
          postHash,
          updatePost,
          onUserBlock,
          isPinned,
          hideStatusRow,
          disableReadMore,
          hideMedia,
          hideEmbeds,
          hideHeader,
          hideAvatar,
          hideUpvote,
          hideHeaderActions,
          hideActivity,
          forwardContextProps,
          postCachePrefix,
          showMoreMaxHeight,
          isNSFW: (post.nsfwCount?.nodes?.length || 0) > 0,
          posterIsNSFW: (post.poster?.isNSFW?.totalCount || 0) > 0,
          posterIsGreylisted: (post.poster?.isGreylisted?.totalCount || 0) > 0,
          posterIsBlacklisted: !!post.poster?.isBlacklisted,
          isComment: !!post.parentPostHash,

          ...getPostData(post),
        }}
      >
        {children}
      </PostContext.Provider>
    </ErrorBoundary>
  );
};

export default PostContext;
